<template>
  <div>
    <div class="air__utils__heading">
      <div class="row">
        <div class="col-4 col-sm-6">
          <h5>
            <router-link to="/devices" class="text-dark"><i class="fa fa-arrow-left mr-2"></i></router-link>
            Device Users
          </h5>
        </div>
        <div class="col-8 col-sm-6 text-right">
          <div class="d-inline-flex flex-row">
            <div class="mr-2">
              <a-button size="large" type="default" icon="reload" @click="syncUsers" :loading="syncing">Sync All</a-button>
            </div>
            <div>
              <a-button size="large" type="primary" icon="plus" @click="showAddModal = true">Add User</a-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="row justify-content-center">
      <div class="col-xl-12">
        <div class="row align-items-center mb-2">
          <!-- Session -->
          <div class="col-md-2 col-12 mb-2">
            <a-select
              size="large"
              v-model="selectedSession"
              optionFilterProp="children"
              style="width: 100%"
              @change="handleSessionChange"
              :filterOption="false"
            >
              <template v-for="session in sessions">
                <a-select-option :value="session.id" :key="session.id">
                  {{ session.name }}
                  <small class="text-primary ml-2">{{ currentSession && currentSession.id === session.id ? 'Current' : '' }}</small>
                </a-select-option>
              </template>
            </a-select>
          </div>
          <!-- Class -->
          <div class="col-md-2 col-12 mb-2">
            <a-spin :spinning="classSpinning">
              <a-select
                size="large"
                v-model="selectedClass"
                placeholder="All Classes"
                optionFilterProp="children"
                style="width: 100%"
                @change="handleClassSelect"
                :filterOption="false"
                :defaultValue="undefined"
              >
                <a-select-option :value="undefined">All Classes</a-select-option>
                <template v-for="schoolClass in schoolClasses">
                  <a-select-option :value="schoolClass.id" :key="schoolClass.id">
                    {{ schoolClass.name }}
                  </a-select-option>
                </template>
              </a-select>
            </a-spin>
          </div>
          <!-- Section -->
          <div class="col-md-2 col-12 mb-2">
            <a-spin :spinning="sectionSpinning">
              <a-select
                size="large"
                v-model="selectedSection"
                placeholder="All Sections"
                optionFilterProp="children"
                style="width: 100%"
                @change="handleSectionChange"
                :filterOption="false"
                :defaultValue="undefined"
                :disabled="!selectedClass"
              >
                <a-select-option :value="undefined">All Sections</a-select-option>
                <template v-for="section in sections">
                  <a-select-option :value="section.id" :key="section.id">
                    {{ section.name }}
                  </a-select-option>
                </template>
              </a-select>
            </a-spin>
          </div>
          <!-- Search -->
          <div class="col-md-6 col-12 mb-2">
            <div class="text-right">
              <a-input-search
                placeholder="Search by Name or ID..."
                size="large"
                v-model="search"
                @search="searchUsers"
                @change="searchUsers"
              />
            </div>
          </div>
        </div>

        <!-- Data Table -->
        <div class="card">
          <div class="card-body">
            <a-table
              :columns="columns"
              :dataSource="filteredUsers"
              :loading="loading"
              :pagination="false"
              :rowKey="record => record.id"
              size="middle"
            >
              <template slot="dataTypes" slot-scope="text, record">
                <a-tag v-for="(type, idx) in record.data_types" :key="idx" color="blue" class="mb-1">
                  {{ type }}
                </a-tag>
              </template>

              <template slot="lastUpdated" slot-scope="text">
                <span v-if="text">{{ text | moment('DD MMM YYYY, hh:mm A') }}</span>
                <span v-else class="text-muted">—</span>
              </template>

              <template slot="actions" slot-scope="text, record">
                <a-dropdown>
                  <a-button size="small">Actions <a-icon type="down" /></a-button>
                  <a-menu slot="overlay">
                    <a-menu-item @click="enableUser(record.user_id)">Enable</a-menu-item>
                    <a-menu-item @click="disableUser(record.user_id)">Disable</a-menu-item>
                    <a-menu-divider />
                    <a-menu-item @click="deleteUser(record.user_id)" style="color: #f5222d;">Delete</a-menu-item>
                  </a-menu>
                </a-dropdown>
              </template>
            </a-table>
          </div>
        </div>

        <!-- Pagination -->
        <div class="text-right">
          <a-pagination
            showSizeChanger
            @showSizeChange="onShowSizeChange"
            :total="filteredUsers.length"
            :pageSize.sync="pageSize"
            v-model="currentPage"
          >
          </a-pagination>
        </div>
      </div>
    </div>

    <!-- Add User Modal -->
    <a-modal title="Add User to Device" :visible="showAddModal" @ok="addUser" @cancel="showAddModal = false" okText="Add User">
      <a-form layout="vertical">
        <a-form-item label="User Type">
          <a-select v-model="newUser.type" style="width: 100%">
            <a-select-option value="Student">Student</a-select-option>
            <a-select-option value="Staff">Staff</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="System ID">
          <a-input v-model="newUser.system_id" placeholder="Enter System ID (e.g. STU123)" />
        </a-form-item>
        <a-form-item label="First Name">
          <a-input v-model="newUser.first_name" />
        </a-form-item>
        <a-form-item label="Last Name">
          <a-input v-model="newUser.last_name" />
        </a-form-item>
      </a-form>
    </a-modal>

  </div>
</template>

<script>
import { debounce, find } from 'lodash'

export default {
  name: 'DeviceUsers',
  data() {
    return {
      deviceId: this.$route.params.id,
      users: [],
      loading: false,
      syncing: false,
      currentPage: 1,
      pageSize: 30,
      showAddModal: false,
      sessions: [],
      currentSession: null,
      schoolClasses: [],
      sections: [],
      selectedSession: undefined,
      selectedClass: undefined,
      selectedSection: undefined,
      classSpinning: false,
      sectionSpinning: false,
      search: null,
      columns: [
        { title: 'User ID', dataIndex: 'user_id', key: 'user_id', sorter: (a, b) => (a.user_id || '').localeCompare(b.user_id || ''), width: 100 },
        { title: 'Student Code', dataIndex: 'student_code', key: 'student_code', sorter: (a, b) => (a.student_code || '').localeCompare(b.student_code || ''), width: 120 },
        { title: 'First Name', dataIndex: 'first_name', key: 'first_name', sorter: (a, b) => (a.first_name || '').localeCompare(b.first_name || '') },
        { title: 'Last Name', dataIndex: 'last_name', key: 'last_name', sorter: (a, b) => (a.last_name || '').localeCompare(b.last_name || '') },
        { title: 'Type', dataIndex: 'user_type', key: 'user_type', width: 80 },
        { title: 'Class/Section', dataIndex: 'class_section', key: 'class_section', width: 140 },
        { title: 'Enrolled Data', key: 'data_types', scopedSlots: { customRender: 'dataTypes' }, width: 160 },
        { title: 'Last Updated', dataIndex: 'last_updated', key: 'last_updated', scopedSlots: { customRender: 'lastUpdated' }, width: 180 },
        { title: 'Actions', key: 'actions', scopedSlots: { customRender: 'actions' }, align: 'right', width: 100 },
      ],
      newUser: {
        type: 'Student',
        system_id: '',
        first_name: '',
        last_name: '',
      },
    }
  },
  computed: {
    filteredUsers() {
      if (!this.search) return this.users
      const s = this.search.toLowerCase()
      return this.users.filter(u => {
        return (u.first_name && u.first_name.toLowerCase().includes(s)) ||
               (u.last_name && u.last_name.toLowerCase().includes(s)) ||
               (u.user_id && u.user_id.toLowerCase().includes(s)) ||
               (u.student_code && u.student_code.toLowerCase().includes(s))
      })
    },
  },
  mounted() {
    this.$http.get('/api/session')
      .then(({ data }) => {
        this.sessions = data.data
        this.currentSession = find(data.data, ['is_current', 1])
        if (this.currentSession) {
          this.selectedSession = this.currentSession.id
        }
        this.getClasses()
        this.fetchUsers()
      })
  },
  created() {
    this.searchUsers = debounce(this.searchUsers, 500)
  },
  methods: {
    fetchUsers() {
      this.loading = true
      this.$http.get(`/api/devices/${this.deviceId}/users`, {
        params: {
          session_id: this.selectedSession,
          class_id: this.selectedClass,
          section_id: this.selectedSection,
        },
      })
        .then(response => {
          this.users = response.data
        })
        .catch(() => {
          this.$notification.error({ message: 'Error', description: 'Failed to fetch users' })
        })
        .finally(() => {
          this.loading = false
        })
    },
    searchUsers(e) {
      if (e && e.target) {
        this.search = e.target.value
      }
    },
    handleSessionChange(value) {
      this.selectedSession = value
      this.selectedSection = undefined
      this.selectedClass = undefined
      this.getClasses()
      this.fetchUsers()
    },
    getClasses() {
      this.classSpinning = true
      this.$http.get('/api/class', {
        params: { session_id: this.selectedSession },
      }).then(({ data }) => {
        this.schoolClasses = data.data
        this.classSpinning = false
      })
    },
    handleClassSelect(value) {
      this.selectedSection = undefined
      if (value) {
        this.sectionSpinning = true
        this.getClassSections(value)
      }
      this.fetchUsers()
    },
    getClassSections(value) {
      this.$http.get(`/api/section/class/${value}`, {
        params: { session_id: this.selectedSession },
      }).then(({ data }) => {
        this.sections = data.data
        this.sectionSpinning = false
      })
    },
    handleSectionChange(value) {
      this.selectedSection = value || undefined
      this.fetchUsers()
    },
    syncUsers() {
      this.syncing = true
      this.$http.post(`/api/devices/${this.deviceId}/users/sync`)
        .then(() => {
          this.$notification.success({ message: 'Sync Started', description: 'Users will be synchronized in the background' })
        })
        .catch(() => {
          this.$notification.error({ message: 'Error', description: 'Failed to start sync' })
        })
        .finally(() => {
          this.syncing = false
        })
    },
    addUser() {
      this.$http.post(`/api/devices/${this.deviceId}/users/add`, {
        user_id: this.newUser.system_id,
        first_name: this.newUser.first_name,
        last_name: this.newUser.last_name,
        user_type: this.newUser.type,
      })
        .then(() => {
          this.$notification.success({ message: 'Success', description: 'Command sent to add user' })
          this.showAddModal = false
        })
        .catch(() => {
          this.$notification.error({ message: 'Error', description: 'Failed to add user' })
        })
    },
    deleteUser(userId) {
      this.$confirm({
        title: `Are you sure you want to delete user ${userId}?`,
        okText: 'Yes, Delete',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: () => {
          this.$http.delete(`/api/devices/${this.deviceId}/users/${userId}`)
            .then(() => {
              this.$notification.success({ message: 'Success', description: 'User deleted' })
              this.fetchUsers()
            })
            .catch(() => {
              this.$notification.error({ message: 'Error', description: 'Failed to delete user' })
            })
        },
      })
    },
    enableUser(userId) {
      this.$http.post(`/api/devices/${this.deviceId}/users/${userId}/enable`)
        .then(() => this.$notification.success({ message: 'Success', description: 'Command sent to enable user' }))
    },
    disableUser(userId) {
      this.$http.post(`/api/devices/${this.deviceId}/users/${userId}/disable`)
        .then(() => this.$notification.success({ message: 'Success', description: 'Command sent to disable user' }))
    },
    onShowSizeChange(current, pageSize) {
      this.pageSize = pageSize
      this.currentPage = current
    },
  },
}
</script>
